/* GrowthOS — Create/Edit Opportunity page.
   Same shell as addLead.jsx/addContactsForm.jsx/addAccountsForm.jsx (sticky header,
   cover with identity + readiness card + real photo upload, config-driven form
   sections below via dealForm.jsx) — just the fields/required set match Opportunity
   instead of Lead/Contact/Account. Photo upload matches the others' cover exactly
   (same `.gos-cl-photo*` widget/handlers) — the `*LeadImageCRM` service functions
   are generic Azure-blob helpers keyed by a `${module}/${id}` path (not actually
   Lead-specific despite the name); this page already called them with
   `module: 'Opportunity'` before the re-skin (previously via the shared
   `HeaderProfileBlock`), so the capability isn't new — only the widget UI changed.
   Business logic (handleChangeForm's numeric parsing, fetchContactsByAccountId,
   handleFormSubmit) is carried over unchanged from the legacy addDeals.jsx, with one
   small fix: handleLookupDropDownChange's accountId/contactId branches now set
   `account`/`contact` (matching every other field's `<bareName>` convention, and
   what dealForm.jsx's new Select actually reads) instead of the old
   `accountName`/`contactName` keys, which nothing else in the app read. */
import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { initValueOpportunity } from '../../shared/initials';
import apiServices from '../../services/service';
import { toast } from 'react-toastify';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import { fetchDataAndUpdateStore, singleRowAddUpdateInStore } from '../../redux/actions';
import DealForm from './dealForm';
import { validationSchemaDeal } from './validationSchema';
import { getGridDataByModuleKey, getUser } from '../../redux/selector';
import { dropdownPayload } from '../../shared/constants';

const CL_ICONS = {
  chevL: <path d="M15 18l-6-6 6-6" />,
  check: <path d="M20 6 9 17l-5-5" />,
  user: (<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
  building: <path d="M3 21h18M6 21V4h9v17M9 8h3M9 12h3M9 16h3M15 21V9h4v12" />,
  target: (<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></>),
  image: (<><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9" r="1.5" /><path d="M21 16l-5-5L5 20" /></>),
  refresh: <path d="M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5" />,
  trash: <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6h12M10 11v6M14 11v6" />,
  checkCircle: (<><circle cx="12" cy="12" r="9" /><path d="M8.5 12l2.5 2.5 4.5-5" /></>),
};
const CLIc = ({ name, size = 16, style }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={style}>
    {CL_ICONS[name] || null}
  </svg>
);

const REQUIRED = [
  { key: 'name', label: 'Opportunity Name' },
  { key: 'accountId', label: 'Account' },
  { key: 'contactId', label: 'Contact' },
  { key: 'stageId', label: 'Stage' },
  { key: 'industryId', label: 'Industry' },
  { key: 'leadSourceId', label: 'Lead Source' },
  { key: 'sourceName', label: 'Source Name' },
  { key: 'nextStep', label: 'Next Step' },
];

const AddDeals = () => {
  const gridData = useSelector(getGridDataByModuleKey('opportunityList'));
  const handleCloseAddForm = () => {
    if (location?.state?.from) {
      navigate(-1);
    } else {
      navigate('/opportunity');
    }
  };
  const stateData = useSelector((state) => state);
  const dispatch = useDispatch();
  let storeLoading = isLoadingByKey(
    stateData,
    'revenueTypeList',
    'leadSourceList',
    'industryList',
    'accountList',
    'contactList'
  );

  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useSelector(getUser);
  if (!initValueOpportunity.owner) {
    initValueOpportunity.owner = userInfo?.user?.display_name;
    initValueOpportunity.ownerId = userInfo?.user?.userId;
  }
  const [loading, setLoading] = useState(storeLoading);
  const [formData, setFormData] = useState(initValueOpportunity);
  const [errormsg, setErrorMsg] = useState({});
  const [formEdited, setFormEdited] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [contactOptions, setContactOptions] = useState([]);
  const [imagefile, setImageFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoInputRef = useRef(null);

  const handleUploadImage = async (imagefile) => {
    setImageFile(imagefile);
    setFormEdited(true);
  };

  // show the existing photo when editing
  useEffect(() => {
    if (formData?.id) {
      apiServices
        .getLeadImageCRM(`Opportunity/${formData.id}`)
        .then((url) => setPhotoPreview(url))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.id]);

  const onPhotoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
    handleUploadImage(file);
    e.target.value = '';
  };
  const removePhoto = () => {
    setPhotoPreview(null);
    setImageFile(null);
    setFormEdited(true);
  };

  const saveUploadImage = async (id) => {
    if (imagefile && id) {
      const newFile = new File([imagefile], id.data, {
        type: imagefile.type,
      });
      await apiServices.uploadLeadImageCRM(newFile, 'Opportunity');
    }
  };

  const editUploadImage = async (id) => {
    if (imagefile && id) {
      const newFile = new File([imagefile], id, {
        type: imagefile.type,
      });
      await apiServices.uploadLeadImageCRM(newFile, 'Opportunity');
    }
  };

  const handleChangeForm = (e) => {
    const { name, value } = e.target;
    let convertedValue = value;

    if (['expectedRevenue', 'probability', 'amount'].includes(name)) {
      if (value.trim() === '') {
        convertedValue = null;
      } else {
        let numericString = value.replace(/[^0-9.]/g, '');

        const decimalParts = numericString.split('.');
        if (decimalParts.length > 2) {
          numericString = decimalParts[0] + '.' + decimalParts.slice(1).join('');
        }

        if (numericString.includes('.')) {
          const [intPart, decimalPart = ''] = numericString.split('.');
          numericString = intPart + '.' + decimalPart.slice(0, 2);
        }

        let floatValue = parseFloat(numericString);
        if (isNaN(floatValue) || floatValue < 0) {
          floatValue = null;
        }

        if (name === 'probability' && floatValue > 100) {
          setErrorMsg((prevErrorMsg) => ({
            ...prevErrorMsg,
            probability: 'Probability cannot be greater than 100%',
          }));
          return;
        }

        convertedValue = floatValue;
      }
    }

    setErrorMsg((prevErrorMsg) => ({
      ...prevErrorMsg,
      [name]: undefined,
    }));

    setFormData((prevData) => ({
      ...prevData,
      [name]: convertedValue,
    }));

    setFormEdited(true);
  };

  const fetchContactsByAccountId = (accountId) => {
    const data = {
      moduleName: 'Account',
      id: accountId,
    };
    apiServices
      .getContactByModule(data)
      .then((res) => {
        if (res.status === 200) {
          setContactOptions(res.data.contacts || []);
        } else {
          setContactOptions([]);
        }
      })
      .catch(() => {
        setContactOptions([]);
      });
  };

  const handleChange = (selectedOption, name) => {
    setSelectedOptions((prevState) => ({
      ...prevState,
      [name]: selectedOption,
    }));

    handleLookupDropDownChange(selectedOption.value, selectedOption, name);
  };

  const handleLookupDropDownChange = (selectedId, selectedOption, name) => {
    const selectedValue = selectedOption.label;
    setErrorMsg((prevErrorMsg) => ({
      ...prevErrorMsg,
      [name]: undefined,
    }));
    if (name === 'leadSourceId') {
      setFormData({ ...formData, leadSourceId: selectedId, leadSource: selectedValue });
      setFormEdited(true);
    } else if (name === 'industryId') {
      setFormData({ ...formData, industryId: selectedId, industry: selectedValue });
      setFormEdited(true);
    } else if (name === 'accountId') {
      setFormData({
        ...formData,
        accountId: selectedId,
        account: selectedValue,
        contactId: '',
        contact: '',
      });
      setContactOptions([]);
      fetchContactsByAccountId(selectedId);
      setFormEdited(true);
    } else if (name === 'contactId') {
      setFormData({ ...formData, contactId: selectedId, contact: selectedValue });
      setFormEdited(true);
    } else if (name === 'stageId') {
      setFormData({ ...formData, stageId: selectedId, stage: selectedValue });
      setFormEdited(true);
    } else if (name === 'projectTypeId') {
      setFormData({ ...formData, projectTypeId: selectedId, projectType: selectedValue });
      setFormEdited(true);
    } else if (name === 'forecastCategoryId') {
      setFormData({ ...formData, forecastCategoryId: selectedId, forecastCategory: selectedValue });
      setFormEdited(true);
    } else if (name === 'ownerId') {
      setFormData({ ...formData, ownerId: selectedId, owner: selectedValue });
      setFormEdited(true);
    } else if (name === 'campaignSourceId') {
      setFormData({ ...formData, campaignSourceId: selectedId, campaignSource: selectedValue });
      setFormEdited(true);
    } else if (name === 'reachOutForMarketingId') {
      setFormData({ ...formData, reachOutForMarketing: selectedId });
      setFormEdited(true);
    }
  };

  const handleSubmit = async (formData) => {
    setLoading(true);
    try {
      await validationSchemaDeal.validate(formData, { abortEarly: false });
      handleFormSubmit(formData);
    } catch (error) {
      setLoading(false);
      const fieldErrors = {};
      error?.inner?.forEach((err) => {
        fieldErrors[err.path] = err.message;
      });
      setErrorMsg(fieldErrors);
      const errorFields = Object.keys(fieldErrors);
      if (errorFields.length > 0) {
        const firstErrorKey = errorFields[0];
        const errorRef = refs[firstErrorKey];
        if (errorRef?.current) {
          errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorRef.current.focus();
        }
      }
      console.error('Validation Error:', fieldErrors);
    }
  };
  const refs = {
    name: useRef(null),
    accountId: useRef(null),
    contactId: useRef(null),
    stageId: useRef(null),
    industryId: useRef(null),
    leadSourceId: useRef(null),
    sourceName: useRef(null),
    nextStep: useRef(null),
  };

  const updateList = async (msg, id) => {
    setLoading(false);
    toast.success(msg, { position: 'top-right' });

    try {
      dispatch(
        singleRowAddUpdateInStore({
          moduleName: 'Opportunity',
          id: formData.type === 'add' ? id : formData.id,
          rowIndex: formData.rowIndex,
        })
      );
      dispatch(
        fetchDataAndUpdateStore({
          key: 'opportunityDropdown',
          inputData: dropdownPayload,
        })
      );
      if (formData.from === 'view') {
        navigate(`/opportunity/viewOpportunity?id=${formData.id}&pageNo=${gridData?.inputPayload?.pageNumber}`);
      } else {
        navigate('/opportunity');
      }
    } catch (error) {
      console.error('Failed to fetch and update Opportunity:', error);
      toast.error('Something went wrong while updating the list.', { position: 'top-right' });
    }
  };

  const handleFormSubmit = (formData) => {
    if (formData.type === 'add') {
      const sanitizedFormData = { ...formData };
      delete sanitizedFormData.type;
      apiServices
        .createOpportunityList(sanitizedFormData)
        .then((response) => {
          if (response.status === 200) {
            const id = response.data;
            saveUploadImage(response);
            updateList('Opportunity created successfully!', id);
          } else {
            setLoading(false);
            const errorMessages = [];
            for (const key in response.errors) {
              if (response.errors.hasOwnProperty(key)) {
                const messages = response.errors[key];
                errorMessages.push(...messages);
              }
            }
            errorMessages.forEach((errorMessage) => {
              toast.error(errorMessage, { position: 'top-right' });
            });
          }
        })
        .catch((error) => {
          console.error('Error:', error, 'DataFormat:', formData);
          toast.error('An unexpected error occurred.', { position: 'top-right' });
          setLoading(false);
        });
    } else {
      editUploadImage(formData.id);
      const sanitizedFormData = { ...formData };
      delete sanitizedFormData.type;
      apiServices
        .updateOpportunityList(sanitizedFormData)
        .then((response) => {
          if (response.status === 200) {
            updateList('Opportunity updated successfully!');
          } else {
            setLoading(false);
            const errorMessages = [];
            for (const key in response.errors) {
              if (response.errors.hasOwnProperty(key)) {
                const messages = response.errors[key];
                errorMessages.push(...messages);
              }
            }
            errorMessages.forEach((errorMessage) => {
              toast.error(errorMessage, { position: 'top-right' });
            });
          }
        })
        .catch((error) => {
          console.error('Error:', error, 'DataFormat:', formData);
          toast.error('An unexpected error occurred.', { position: 'top-right' });
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useEffect(() => {
    let updatedFormData = {
      ...formData,
      type: location.pathname.includes('addOpportunity') ? 'add' : 'edit',
    };
    if (location.pathname.includes('editOpportunity') && location.state) {
      updatedFormData = {
        ...location.state,
        type: 'edit',
      };
    }
    setFormData(updatedFormData);
  }, [location]);

  // fetch the contact list for an existing account when opening in edit mode
  useEffect(() => {
    if (formData?.type === 'edit' && formData?.accountId) {
      fetchContactsByAccountId(formData.accountId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.type, formData?.accountId]);

  const isEdit = formData?.type === 'edit';
  const displayName = formData?.name || (isEdit ? 'Opportunity' : 'Untitled opportunity');
  const idSub = [formData?.account, formData?.stage].filter(Boolean).join(' · ');
  const filledReq = REQUIRED.filter((r) => {
    const v = formData?.[r.key];
    return v !== null && v !== undefined && String(v).trim() !== '';
  }).length;
  const pct = Math.round((filledReq / REQUIRED.length) * 100);
  const allReady = filledReq === REQUIRED.length;

  return (
    <div className="addlead_container gos-cl">
      {/* sticky header */}
      <div className="gos-cl-header">
        <button type="button" className="gos-cl-back" onClick={handleCloseAddForm} title="Back to Opportunities">
          <CLIc name="chevL" size={18} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="gos-cl-htitle">{isEdit ? 'Edit Opportunity' : 'New Opportunity'}</div>
          <div className="gos-cl-hsub">
            Fields marked <span className="gos-cl-reqdot">*</span> are required
          </div>
        </div>
        <button type="button" className="gos-btn gos-btn-ghost" onClick={handleCloseAddForm}>Cancel</button>
        <button
          type="button"
          className="gos-btn gos-btn-primary"
          onClick={() => handleSubmit(formData)}
          disabled={loading || (isEdit && !formEdited)}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving…
            </>
          ) : (
            <>
              <CLIc name="check" size={16} /> Save Opportunity
            </>
          )}
        </button>
      </div>

      <div className="gos-cl-scroll">
        <div className="gos-cl3-body">
          {/* cover */}
          <div className="gos-cl3-cover">
            <div className="gos-cl-photo-wrap">
              <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPhotoPick} />
              <div
                className={'gos-cl-photo' + (photoPreview ? ' has-img' : '')}
                onClick={photoPreview ? undefined : () => photoInputRef.current?.click()}
              >
                {photoPreview ? (
                  <>
                    <img src={photoPreview} alt="Opportunity" />
                    <div className="gos-cl-photo-overlay" onClick={() => photoInputRef.current?.click()}>Change</div>
                  </>
                ) : (
                  <div className="gos-cl-photo-empty">
                    <CLIc name="image" size={24} />
                    <span>Add photo</span>
                  </div>
                )}
              </div>
              {photoPreview ? (
                <div className="gos-cl-photo-actions">
                  <button type="button" className="gos-cl-photo-act" onClick={() => photoInputRef.current?.click()}>
                    <CLIc name="refresh" size={12} /> Change
                  </button>
                  <button type="button" className="gos-cl-photo-act danger" onClick={removePhoto}>
                    <CLIc name="trash" size={12} /> Remove
                  </button>
                </div>
              ) : (
                <div className="gos-cl-photo-hint">JPG or PNG · click to upload</div>
              )}
            </div>

            <div className="gos-cl3-id">
              <div className="gos-cl3-id-eyebrow">{isEdit ? 'Edit Opportunity' : 'New Opportunity'}</div>
              <div className={'gos-cl3-id-name' + (formData?.name ? '' : ' ph')}>{displayName}</div>
              <div className="gos-cl3-id-sub">{idSub || 'Add deal and pipeline details below'}</div>
              <div className="gos-cl3-id-chips">
                <span className="gos-lr-chip"><CLIc name="user" size={13} /> Owner: {formData?.owner || 'Unassigned'}</span>
                {formData?.account ? <span className="gos-lr-chip"><CLIc name="building" size={13} /> {formData.account}</span> : null}
                {formData?.stage ? <span className="gos-lr-chip"><CLIc name="target" size={13} /> {formData.stage}</span> : null}
              </div>
            </div>

            <div className={'gos-cl3-ready' + (allReady ? ' full' : '')}>
              <div className="gos-cl3-ready-top">
                <span className="gos-cl3-ready-label">Opportunity readiness</span>
                <span className="gos-cl3-ready-pct">{pct}%</span>
              </div>
              <div className="gos-cl3-bar"><div style={{ width: pct + '%' }} /></div>
              <div className={'gos-cl3-ready-sub' + (allReady ? ' ok' : '')}>
                {allReady ? (
                  <><CLIc name="checkCircle" size={13} /> All required fields complete</>
                ) : (
                  `${filledReq} of ${REQUIRED.length} required fields filled`
                )}
              </div>
            </div>
          </div>

          <DealForm
            formData={formData}
            handleChangeForm={handleChangeForm}
            handleChange={handleChange}
            errormsg={errormsg}
            refs={refs}
            contactOptions={contactOptions}
          />
        </div>
      </div>
    </div>
  );
};

export default AddDeals;
